/**
 * Cashflow Aggregator for Visualization
 *
 * Transforms complex cashflow data into yearly summaries for charts
 */

import type { Candidate, CashFlow } from '~/types/candidate';

/**
 * Yearly payment breakdown for stacked bar chart
 */
export interface YearlyPaymentData {
  year: number;
  existingDebt: number;
  bondSeries: {
    [bondSeriesId: string]: {
      name: string;
      payment: number;
    };
  };
  total: number;
}

/**
 * Aggregate cashflows by tax year for visualization
 * Groups all payments within a tax year and sums by category
 */
export function aggregateCashflowsByYear(candidate: Candidate | null): YearlyPaymentData[] {
  if (!candidate?.cashFlows || candidate.cashFlows.length === 0) {
    return [];
  }

  // Group cashflows by tax year
  const yearlyData = new Map<number, YearlyPaymentData>();

  candidate.cashFlows.forEach((flow: CashFlow) => {
    const year = flow.relatedTaxBillYear;

    // Initialize year data if not exists
    if (!yearlyData.has(year)) {
      yearlyData.set(year, {
        year,
        existingDebt: 0,
        bondSeries: {},
        total: 0,
      });
    }

    const yearData = yearlyData.get(year)!;

    // Add existing debt payment
    if (flow.debtPaymentAmount) {
      yearData.existingDebt += flow.debtPaymentAmount;
    }

    // Add bond series payments
    if (flow.bondPayments && flow.bondPayments.length > 0) {
      flow.bondPayments.forEach((bondPayment) => {
        const bondId = bondPayment.bondSeriesId;
        const bondName = bondPayment.bondSeriesName;
        const payment = bondPayment.totalPayment || 0;

        if (!yearData.bondSeries[bondId]) {
          yearData.bondSeries[bondId] = {
            name: bondName,
            payment: 0,
          };
        }

        yearData.bondSeries[bondId].payment += payment;
      });
    }

    // Calculate total
    yearData.total =
      yearData.existingDebt +
      Object.values(yearData.bondSeries).reduce((sum, bond) => sum + bond.payment, 0);
  });

  // Convert to array and sort by year
  return Array.from(yearlyData.values()).sort((a, b) => a.year - b.year);
}

/**
 * Convert yearly payment data to format suitable for Recharts stacked bar
 * Flattens the bondSeries object into individual properties
 */
export interface StackedBarChartData {
  year: number;
  existingDebt: number;
  [key: string]: number; // Dynamic bond series keys
}

export function formatForStackedBarChart(yearlyData: YearlyPaymentData[]): {
  data: StackedBarChartData[];
  series: Array<{ key: string; name: string; color: string }>;
} {
  // Define color palette
  const colors = {
    existingDebt: '#718096', // Gray for existing debt
    bond1: '#3182CE', // Blue
    bond2: '#38A169', // Green
    bond3: '#D69E2E', // Yellow
    bond4: '#E53E3E', // Red
    bond5: '#805AD5', // Purple
  };

  // Track all unique bond series
  const bondSeriesMap = new Map<string, { name: string; index: number }>();
  let bondIndex = 0;

  // First pass: identify all unique bond series
  yearlyData.forEach((year) => {
    Object.entries(year.bondSeries).forEach(([bondId, bond]) => {
      if (!bondSeriesMap.has(bondId)) {
        bondSeriesMap.set(bondId, {
          name: bond.name,
          index: bondIndex++,
        });
      }
    });
  });

  // Format data for chart
  const chartData: StackedBarChartData[] = yearlyData.map((year) => {
    const dataPoint: StackedBarChartData = {
      year: year.year,
      existingDebt: year.existingDebt,
    };

    // Add each bond series as a property
    bondSeriesMap.forEach((bondInfo, bondId) => {
      const bondKey = `bond${bondInfo.index + 1}`;
      dataPoint[bondKey] = year.bondSeries[bondId]?.payment || 0;
    });

    return dataPoint;
  });

  // Build series configuration
  const series = [{ key: 'existingDebt', name: 'Existing Debt', color: colors.existingDebt }];

  bondSeriesMap.forEach((bondInfo) => {
    const bondKey = `bond${bondInfo.index + 1}`;
    const colorKey = bondKey as keyof typeof colors;
    series.push({
      key: bondKey,
      name: bondInfo.name,
      color: colors[colorKey] || '#9CA3AF', // Fallback gray
    });
  });

  return { data: chartData, series };
}
